/**
 * Deterministic hash-based value noise for the terrain bake pass.
 *
 * Per-cell brightness variation seeded by (x, y, terrainId). Same
 * inputs → same output on every host, matching the sim's determinism
 * discipline even though this is client-only (visual consistency
 * during MP screenshots + replays).
 *
 * Not a Perlin / simplex noise — a simple bit-mixed hash producing a
 * uniform-ish value in [-1, 1] per cell. Cheap enough to bake at every
 * cell of a 320×200 map in a single pass.
 */

/** 32-bit bit-mix hash inspired by splitmix32; returns a uint32. */
function hash32(x: number, y: number, salt: number): number {
  let h = (x | 0) * 0x27d4eb2d;
  h = ((h ^ ((y | 0) * 0x165667b1)) >>> 0) + (salt | 0);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  h ^= h >>> 15;
  return h >>> 0;
}

/**
 * Value noise at cell (x, y) with a terrain-id salt so different terrain
 * types get uncorrelated variation. Returns a signed value in [-1, 1].
 */
export function cellNoise(x: number, y: number, terrainId: number): number {
  return (hash32(x, y, terrainId) / 0xffffffff) * 2 - 1;
}

/**
 * Apply a subtle noise-driven brightness offset to an RGB triplet.
 * amplitude of 0.1 = ±10% brightness variation. Clamps into [0, 255].
 */
export function applyNoiseToRgb(
  r: number,
  g: number,
  b: number,
  x: number,
  y: number,
  terrainId: number,
  amplitude: number,
): [number, number, number] {
  const noise = cellNoise(x, y, terrainId);
  const delta = noise * amplitude;
  const nr = Math.max(0, Math.min(255, Math.round(r * (1 + delta))));
  const ng = Math.max(0, Math.min(255, Math.round(g * (1 + delta))));
  const nb = Math.max(0, Math.min(255, Math.round(b * (1 + delta))));
  return [nr, ng, nb];
}
